import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cloud, Server, Activity, Power, ExternalLink, Terminal, Plus, Cpu, HardDrive, MoreVertical, Search, Filter, Download } from 'lucide-react';
import BillingModal from './BillingModal';
import PodDetailsModal from './PodDetailsModal';

const T = {
  bg: '#F8FAFC',
  panel: '#FFFFFF',
  textMain: '#0F172A',
  textMuted: '#64748B',
  border: '#E2E8F0',
  accent: '#7C3AED',
  accentHover: '#6D28D9',
  success: '#10B981',
  danger: '#EF4444',
  shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
};

function Gauge({ Icon, value = 0 }) {
  const radius = 22;
  const strokeWidth = 4.5;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75;
  const gapLength = circumference * 0.25;
  
  const safeValue = Math.min(Math.max(value || 0, 0), 100);
  const filledLength = arcLength * (safeValue / 100);
  const emptyLength = circumference - filledLength;

  return (
    <div style={{ position: 'relative', width: '56px', height: '56px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {/* Background Track Arc */}
      <svg style={{ position: 'absolute', top: 0, left: 0 }} width="56" height="56" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={radius} fill="transparent" stroke="#E5E7EB" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={`${arcLength} ${gapLength}`} transform="rotate(135 28 28)" />
      </svg>
      {/* Foreground Value Arc */}
      <svg style={{ position: 'absolute', top: 0, left: 0 }} width="56" height="56" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={radius} fill="transparent" stroke={safeValue > 0 ? T.accent : "transparent"} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={`${filledLength} ${emptyLength}`} transform="rotate(135 28 28)" />
      </svg>
      {/* Inner Icon and Text */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', paddingTop: '3px' }}>
        <Icon size={14} color={T.textMuted} />
        <span style={{ 
          fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"', 
          fontSize: '13px', 
          color: safeValue > 0 ? T.accent : '#000000', 
          fontWeight: 400, 
          lineHeight: '18px' 
        }}>
          {Math.round(safeValue)}%
        </span>
      </div>
    </div>
  );
}

export default function MyPodsDashboard() {
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [terminating, setTerminating] = useState(null);
  const [error, setError] = useState('');
  const [showBilling, setShowBilling] = useState(false);
  const [selectedPod, setSelectedPod] = useState(null);

  const fetchPods = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/compute/instances', {
        headers: { 'x-user-id': 'demo_user' }
      });
      const data = await res.json();
      
      if (data.data?.myself?.pods) {
        setPods(data.data.myself.pods);
      } else {
        setPods([]);
      }
    } catch (err) {
      setError('Failed to fetch deployed nodes.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPods();
    const interval = setInterval(fetchPods, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const handleTerminate = async (podId) => {
    if (!window.confirm("Are you sure you want to terminate this node? All unsaved data on the container disk will be lost.")) return;
    
    setTerminating(podId);
    try {
      const res = await fetch('/api/compute/terminate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': 'demo_user' },
        body: JSON.stringify({ podId })
      });
      const data = await res.json();
      
      if (data.error) {
        alert(data.error);
      } else {
        alert('Node is terminating...');
        fetchPods();
      }
    } catch (err) {
      alert('Failed to terminate node.');
    } finally {
      setTerminating(null);
    }
  };

  if (loading && pods.length === 0) {
    return (
      <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto', textAlign: 'center', color: T.textMuted }}>
        <Activity className="animate-spin mx-auto mb-4" size={32} color={T.accent} />
        Loading your nodes...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', width: '100%', boxSizing: 'border-box', fontFamily: 'Inter, system-ui, sans-serif', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <button 
          onClick={() => setShowBilling(true)}
          style={{ background: '#6366F1', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Plus size={14} /> Deploy
        </button>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} color={T.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search Pods" 
              style={{ padding: '6px 12px 6px 32px', border: `1px solid ${T.border}`, borderRadius: '4px', fontSize: '13px', width: '200px' }} 
            />
          </div>
          <button 
            style={{ background: '#F3F4F6', border: `1px solid ${T.border}`, color: '#4B5563', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Filter size={14} /> Display
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#FEE2E2', color: '#B91C1C', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>{error}</div>}

      {pods.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '8px', border: `1px solid ${T.border}`, padding: '64px', textAlign: 'center' }}>
          <Server size={48} color={T.border} style={{ margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px', color: '#111827' }}>No Active Nodes</h3>
          <p style={{ color: T.textMuted, margin: 0, fontSize: '14px' }}>You haven't deployed any GPU nodes yet.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '4px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
          {/* Table Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 1.5fr 1.5fr 1fr 40px', padding: '16px 20px', borderBottom: `1px solid ${T.border}`, fontSize: '13px', color: T.textMuted, fontWeight: 500 }}>
            <div>Name ↑</div>
            <div style={{ textAlign: 'center' }}>Utilization</div>
            <div style={{ textAlign: 'center' }}>Memory</div>

            <div>Compute type</div>
            <div>Cost</div>
            <div></div>
          </div>
          
          {/* Table Body */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {pods.map((pod, i) => {
              // Parse actual metrics from RunPod
              const cpuPercent = pod.runtime?.container?.cpuPercent || 0;
              const memPercent = pod.runtime?.container?.memoryPercent || 0;
              const gpus = pod.runtime?.gpus || [];
              const gpuUtil = gpus.length > 0 ? Math.round(gpus.reduce((acc, g) => acc + g.gpuUtilPercent, 0) / gpus.length) : 0;
              const vramUtil = gpus.length > 0 ? Math.round(gpus.reduce((acc, g) => acc + g.memoryUtilPercent, 0) / gpus.length) : 0;
              const diskUtil = 0; // Not explicitly provided in this payload
              
              const gpuName = pod.machine?.gpuDisplayName || pod.name.split('(')[1]?.replace(')','') || (pod.name.includes('Heavy') ? 'H100 SXM5' : 'RTX A4000');
              
              return (
                <motion.div 
                  key={pod.id}
                  onClick={() => setSelectedPod(pod)}
                  whileHover={{ backgroundColor: '#F9FAFB' }}
                  style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '2.5fr 1.5fr 1.5fr 1.5fr 1fr 40px', 
                    padding: '24px 20px', 
                    borderBottom: i === pods.length - 1 ? 'none' : `1px solid ${T.border}`,
                    alignItems: 'center',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {/* Name and ID */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: pod.desiredStatus === 'RUNNING' ? T.success : '#F59E0B' }} />
                      <span style={{ fontFamily: 'Inter, "Inter Fallback", sans-serif', color: 'rgb(0, 0, 0)', fontWeight: 400, fontSize: '14px' }}>{pod.name.split(' ')[0]} {pod.name.split(' ')[1] || ''}</span>
                    </div>
                    <div style={{ color: T.textMuted, fontFamily: 'monospace', fontSize: '13px', paddingLeft: '20px' }}>{pod.id}</div>
                  </div>

                  {/* Utilization Gauges */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                    <Gauge Icon={Cpu} value={cpuPercent} />
                    <Gauge Icon={Terminal} value={gpuUtil} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                    <Gauge Icon={HardDrive} value={memPercent} />
                    <Gauge Icon={Server} value={vramUtil} />
                  </div>

                  {/* Compute Type */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Server size={16} color={T.textMuted} />
                    <span style={{ fontFamily: 'Inter, "Inter Fallback", sans-serif', color: 'rgb(24, 24, 24)', fontWeight: 350, fontSize: '14px' }}>{gpuName} ×1</span>
                  </div>

                  {/* Cost */}
                  <div style={{ fontFamily: 'Inter, "Inter Fallback", sans-serif', color: 'rgb(24, 24, 24)', fontWeight: 350, fontSize: '14px' }}>
                    ${pod.costPerHr}/hr
                  </div>

                  {/* Actions / Menu */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
                    <button 
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', color: T.textMuted }}
                      title="More options"
                    >
                      <MoreVertical size={20} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
          
          {/* Footer */}
          <div style={{ padding: '16px 20px', background: '#F9FAFB', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: T.textMuted }}>
            <div>10 per page ⌄</div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <span>{'<'}</span>
              <span>Page 1 of 1</span>
              <span>{'>'}</span>
            </div>
          </div>
        </div>
      )}
      {showBilling && <BillingModal onClose={() => setShowBilling(false)} onBalanceUpdated={fetchPods} />}
      {selectedPod && <PodDetailsModal pod={selectedPod} onClose={() => setSelectedPod(null)} />}
    </div>
  );
}
